import {
    IconActivity,
    IconBell,
    IconHome,
    IconLoader,
    IconModule1On1,
    IconModule360,
    IconModuleObjectives,
    IconModulePraise,
    IconModuleReviews,
    IconSettings,
    IconSparkles,
    IconTeam,
} from 'featherico';
import type { Observable } from 'ladda-observable';
import { negate, stubTrue } from 'lodash/fp';
import type * as React from 'react';
import type { Feature, UserCompanyDto } from '../../../../../types/CompanyDtos';
import type NotificationCountDto from '../../../../../types/NotificationCountDto';
import type { CurrentUserDto } from '../../../../../types/UserDtos';
import api from '../../../../api';
import * as R from '../../../../routes';
import { NAVIGATION } from '../../../intercom/intercom-data-targets';
import { isFeatureEnabledForUser } from '../../../services/Features';
import r from '../../../services/Replacement';
import { hasBasicHr, isAnyAdmin, isGuest, isHr } from '../../../services/User';
import type { EapState } from '../../Admin/EarlyAccessProgram/useEapState';
import { countAdminErrors } from '../../Admin/Overview/service';
import { shouldShowAiCoach } from '../../AiCoach/aiFeatureLimits';
import { showGeneralFeedbackWording } from '../../YourFeedback/services/feedback';

const featureStatus =
    (...features: Feature[]) =>
    (enabledFeatures: UserCompanyDto['enabledFeatures']) =>
        features.some(feature => enabledFeatures[feature]);

interface Link {
    title: string;
    route: string;
    alsoMatch?: string[];
    icon: React.ReactNode;
    /** For admins false will give faded-out appearance, for others will filter out the link */
    isFeatureEnabled?: (enabledFeatures: UserCompanyDto['enabledFeatures']) => boolean;
    /** Determine whether the link is visible in the navigation */
    isAvailableTo: (currentUser: CurrentUserDto) => boolean;
    notifier?: null | Notifier;
    titleClass?: string;
    trackingLabel: string;
    testId: string; // These IDs are separate from tracking and intercom IDs to avoid tests breaking when they change.
    /** When set, admins will see a pillbox beside the link.  */
    getStatusLabel?: (currentUser: CurrentUserDto) => Promise<string | undefined>;
    dataIntercomTarget?: string;
    onClick?: (e: React.MouseEvent) => void;
    openInNewTab?: boolean;
}

interface Notifier<T = any> {
    observe: () => Observable<T>;
    toNotification: (r: T) => { count: number; color: string };
}

interface Group {
    className?: string;
    links: Link[];
}

export const links = (eapState: EapState): Group[] => [
    {
        className: '',
        links: [
            {
                title: 'Home',
                route: R.BASE + R.DASHBOARD,
                icon: <IconHome />,
                isAvailableTo: negate(isGuest),
                notifier: null,
                trackingLabel: 'home',
                testId: 'home',
            },
            {
                title: 'Inbox',
                route: R.BASE + R.INBOX,
                icon: <IconBell />,
                isAvailableTo: user => !isGuest(user) && isFeatureEnabledForUser('FEATURE_inboxPage', user),
                notifier: {
                    observe: () => api.notificationsCount.getCount.createObservable(),
                    toNotification: ({ count }: NotificationCountDto) => ({
                        count,
                        color: 'RED',
                    }),
                } as Notifier<NotificationCountDto>,
                trackingLabel: 'inbox',
                testId: 'inbox',
            },
            {
                title: r('praise.menu.title'),
                route: R.BASE + R.PRAISE_STREAM,
                icon: <IconModulePraise />,
                isFeatureEnabled: featureStatus('praise'),
                isAvailableTo: negate(isGuest),
                notifier: null,
                trackingLabel: 'praise',
                testId: 'praise',
                dataIntercomTarget: NAVIGATION.EMPLOYEE_PRAISE,
            },
            {
                title: r('meeting.name.plural'),
                route: R.BASE + R.MEETING_OVERVIEW,
                alsoMatch: [R.BASE + R.MEETING_DEDICATED],
                icon: <IconModule1On1 />,
                isFeatureEnabled: featureStatus('meetings'),
                isAvailableTo: negate(isGuest),
                notifier: null,
                trackingLabel: 'meetings',
                testId: 'meetings',
                dataIntercomTarget: NAVIGATION.EMPLOYEE_MEETINGS,
            },
        ],
    },
    {
        links: [
            {
                title: r('surveys.plural.big'),
                route: R.BASE + R.YOUR_SURVEYS,
                icon: <IconActivity />,
                isFeatureEnabled: f => f.surveys,
                isAvailableTo: user => (user.isGuest ? false : isFeatureEnabledForUser('surveys', user) || isHr(user)),
                notifier: null,
                trackingLabel: 'surveys',
                testId: 'surveys',
                dataIntercomTarget: NAVIGATION.EMPLOYEE_SURVEYS,
            },
            {
                title: r('objective.plural.title'),
                route: R.BASE + R.OBJECTIVES,
                icon: <IconModuleObjectives />,
                isFeatureEnabled: featureStatus('objective'),
                isAvailableTo: negate(isGuest),
                notifier: null,
                trackingLabel: 'objectives',
                testId: 'objectives',
                dataIntercomTarget: NAVIGATION.EMPLOYEE_OBJECTIVES,
            },
            {
                title: showGeneralFeedbackWording ? r('fr.menu.unified.title') : r('fr.menu.title'),
                route: R.BASE + R.FEEDBACK_USER,
                alsoMatch: [R.BASE + R.FEEDBACK_USER_TODO],
                icon: <IconModule360 />,
                isFeatureEnabled: featureStatus('feedback', 'requestFeedback'),
                isAvailableTo: stubTrue,
                notifier: null,
                trackingLabel: '360s',
                testId: '360s',
                dataIntercomTarget: NAVIGATION.EMPLOYEE_FEEDBACK,
            },
            {
                title: r('pr.menu.title'),
                route: R.BASE + R.REVIEWS,
                alsoMatch: ['/appraisalView', '/appraisalDetails'],
                icon: <IconModuleReviews />,
                isFeatureEnabled: featureStatus('review'),
                isAvailableTo: negate(isGuest),
                notifier: null,
                trackingLabel: 'reviews',
                testId: 'reviews',
                dataIntercomTarget: NAVIGATION.EMPLOYEE_REVIEWS,
            },
        ],
    },
    {
        className: '',
        links: [
            {
                title: 'People',
                route: R.BASE + R.PEOPLE,
                icon: <IconTeam />,
                isAvailableTo: u => !isGuest(u),
                notifier: null,
                trackingLabel: 'people',
                testId: 'people',
            },
            {
                title: 'AI Coach',
                route: R.BASE + R.AI_COACH,
                icon: <IconSparkles />,
                isAvailableTo: () => shouldShowAiCoach('workspace'),
                trackingLabel: 'employeeAiCoach',
                testId: 'employeeAiCoach',
            },
            {
                title: 'Settings',
                route: R.BASE + R.SETTINGS,
                alsoMatch: [R.BASE + R.INSIGHTS],
                icon: <IconSettings />,
                isAvailableTo: u => isAnyAdmin(u),
                notifier: {
                    observe: () => api.admin.getSettingsLinks.createObservable(),
                    toNotification: l => ({ count: countAdminErrors(l), color: 'RED' }),
                },
                trackingLabel: 'adminSettings',
                testId: 'adminSettings',
            },
            {
                title: 'Coming Soon',
                route: R.BASE + R.COMING_SOON,
                icon: <IconLoader />,
                isAvailableTo: u =>
                    eapState !== 'HAS_NONE' &&
                    hasBasicHr(u) &&
                    isFeatureEnabledForUser('FEATURE_earlyAccessProgram', u),
                titleClass: eapState === 'HAS_NEW' ? 'coming-soon' : '',
                trackingLabel: 'adminEarlyAccessProgram',
                testId: 'adminEarlyAccessProgram',
            },
        ],
    },
];
